import React, { useState, useEffect } from 'react';
import { 
  Palette, Save, Eye, Layout, Type, Square, Circle, 
  Loader2, Check, X, Plus, Trash2, RotateCcw, Sparkles,
  User, MapPin, Briefcase, GraduationCap, Heart, MessageSquare,
  Crown, Shield, Wifi
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Profile Card Preview Component
const ProfileCardPreview = ({ template, user }) => {
  const getPhotoSizeClass = () => {
    switch (template.photo_size) {
      case 'small': return 'w-16 h-16';
      case 'large': return 'w-full h-48';
      case 'full': return 'w-full h-64';
      default: return 'w-24 h-24';
    }
  };

  const getPhotoShapeClass = () => {
    switch (template.photo_shape) {
      case 'circle': return 'rounded-full';
      case 'square': return 'rounded-none';
      default: return 'rounded-xl';
    }
  };

  const getBorderRadiusClass = () => {
    switch (template.border_radius) {
      case 'none': return 'rounded-none';
      case 'sm': return 'rounded-sm';
      case 'md': return 'rounded-md';
      case 'lg': return 'rounded-lg';
      case 'xl': return 'rounded-xl';
      case 'full': return 'rounded-3xl';
      default: return 'rounded-lg';
    }
  };

  const getShadowClass = () => {
    switch (template.shadow) {
      case 'none': return 'shadow-none';
      case 'sm': return 'shadow-sm';
      case 'lg': return 'shadow-lg';
      case 'xl': return 'shadow-xl';
      default: return 'shadow-md';
    }
  };

  const getNameSizeClass = () => {
    switch (template.name_font_size) {
      case 'sm': return 'text-sm';
      case 'md': return 'text-base';
      case 'xl': return 'text-xl';
      default: return 'text-lg';
    }
  };

  const getNameWeightClass = () => {
    switch (template.name_font_weight) {
      case 'normal': return 'font-normal';
      case 'medium': return 'font-medium';
      case 'bold': return 'font-bold';
      default: return 'font-semibold';
    }
  };

  const getDetailSizeClass = () => {
    switch (template.detail_font_size) {
      case 'xs': return 'text-xs';
      case 'md': return 'text-base';
      default: return 'text-sm';
    }
  };

  const getPaddingClass = () => {
    return `p-${template.padding || '4'}`;
  };

  const enabledFields = (template.fields || [])
    .filter(f => f.enabled)
    .sort((a, b) => a.order - b.order);

  const getFieldValue = (fieldId) => {
    switch (fieldId) {
      case 'age': return `${user.age} years`;
      case 'location': return user.location;
      case 'profession': return user.profession;
      case 'education': return user.education;
      case 'religion': return user.religion;
      default: return '';
    }
  };

  const getFieldIcon = (fieldId) => {
    switch (fieldId) {
      case 'age': return User;
      case 'location': return MapPin;
      case 'profession': return Briefcase;
      case 'education': return GraduationCap;
      case 'religion': return Heart;
      default: return User;
    }
  };

  const isHorizontal = template.layout === 'horizontal';
  const isPhotoOverlay = template.layout === 'photo_overlay';

  // Photo Overlay Layout - Full photo with info at bottom
  if (isPhotoOverlay) {
    return (
      <div
        className={`overflow-hidden transition-all ${getBorderRadiusClass()} ${getShadowClass()} relative`}
        style={{
          maxWidth: '280px'
        }}
      >
        {/* Full Background Photo */}
        <div className="relative">
          <img
            src={user.photo_url}
            alt={user.name}
            className="w-full h-72 object-cover"
          />
          
          {/* Gradient Overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0) 70%)'
            }}
          />

          {/* Top Badges */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {template.show_premium_badge && user.is_premium && (
              <div 
                className="px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium"
                style={{ backgroundColor: template.accent_color, color: '#ffffff' }}
              >
                <Crown className="w-3 h-3" />
                Premium
              </div>
            )}
            {template.show_verified_badge && user.is_verified && (
              <div className="px-2 py-1 bg-blue-500 rounded-full flex items-center gap-1 text-xs font-medium text-white">
                <Shield className="w-3 h-3" />
                Verified
              </div>
            )}
          </div>

          {/* Online Status */}
          {template.show_online_status && user.is_online && (
            <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-green-500 rounded-full text-xs text-white font-medium">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Online
            </div>
          )}

          {/* Bottom Content */}
          <div className="absolute bottom-0 left-0 right-0 p-4" style={{ color: template.text_color }}>
            {/* Name */}
            <h3 
              className={`${getNameSizeClass()} ${getNameWeightClass()} mb-1`}
              style={{ color: template.name_color || template.text_color }}
            >
              {user.name}
            </h3>

            {/* Basic Info */}
            <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${getDetailSizeClass()}`} style={{ opacity: 0.9 }}>
              {enabledFields.map(field => {
                const Icon = getFieldIcon(field.field_id);
                const value = getFieldValue(field.field_id);
                if (!value) return null;
                return (
                  <div key={field.field_id} className="flex items-center gap-1">
                    <Icon className="w-3.5 h-3.5" style={{ color: template.accent_color }} />
                    <span>{value}</span>
                  </div>
                );
              })}
            </div>

            {/* Interest Button */}
            {template.show_interest_button && (
              <button
                className={`mt-3 w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2`}
                style={{
                  backgroundColor: template.button_style === 'filled' ? template.accent_color : 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  borderWidth: template.button_style === 'outline' ? '2px' : '0',
                  borderColor: template.accent_color,
                  borderStyle: 'solid',
                  backdropFilter: template.button_style !== 'filled' ? 'blur(8px)' : 'none'
                }}
              >
                <Heart className="w-4 h-4" />
                Send Interest
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard Layout (Vertical/Horizontal)
  return (
    <div
      className={`overflow-hidden transition-all ${getBorderRadiusClass()} ${getShadowClass()}`}
      style={{
        backgroundColor: template.background_color,
        borderWidth: `${template.border_width || 1}px`,
        borderColor: template.border_color,
        borderStyle: 'solid',
        maxWidth: isHorizontal ? '400px' : '280px'
      }}
    >
      <div className={`${getPaddingClass()} ${isHorizontal ? 'flex gap-4' : ''}`}>
        {/* Photo */}
        <div className={`relative ${isHorizontal ? 'flex-shrink-0' : 'mb-3'}`}>
          <img
            src={user.photo_url}
            alt={user.name}
            className={`object-cover ${getPhotoSizeClass()} ${getPhotoShapeClass()} ${!isHorizontal && template.photo_size === 'large' ? 'w-full' : isHorizontal ? '' : 'mx-auto'}`}
          />
          
          {/* Online Status */}
          {template.show_online_status && user.is_online && (
            <div 
              className="absolute bottom-1 right-1 w-3 h-3 rounded-full border-2"
              style={{ backgroundColor: '#22c55e', borderColor: template.background_color }}
            />
          )}
        </div>

        {/* Content */}
        <div className={`${isHorizontal ? 'flex-1 min-w-0' : ''}`}>
          {/* Name and Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <h3 
              className={`${getNameSizeClass()} ${getNameWeightClass()} truncate`}
              style={{ color: template.name_color || template.text_color }}
            >
              {user.name}
            </h3>
            {template.show_premium_badge && user.is_premium && (
              <Crown className="w-4 h-4" style={{ color: template.accent_color }} />
            )}
            {template.show_verified_badge && user.is_verified && (
              <Shield className="w-4 h-4 text-blue-500" />
            )}
          </div>

          {/* Fields */}
          <div className={`mt-2 space-y-1 ${getDetailSizeClass()}`} style={{ color: template.text_color, opacity: 0.7 }}>
            {enabledFields.map(field => {
              const Icon = getFieldIcon(field.field_id);
              const value = getFieldValue(field.field_id);
              if (!value) return null;
              return (
                <div key={field.field_id} className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: template.accent_color }} />
                  <span className="truncate">{value}</span>
                </div>
              );
            })}
          </div>

          {/* Bio */}
          {template.show_bio && user.bio && (
            <p 
              className={`mt-2 ${getDetailSizeClass()}`}
              style={{ 
                color: template.text_color, 
                opacity: 0.6,
                display: '-webkit-box',
                WebkitLineClamp: template.bio_max_lines || 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {user.bio}
            </p>
          )}

          {/* Action Button */}
          {template.show_interest_button && (
            <button
              className={`mt-3 w-full py-2 px-4 ${getBorderRadiusClass()} text-sm font-medium transition-colors`}
              style={{
                backgroundColor: template.button_style === 'filled' ? template.accent_color : 'transparent',
                color: template.button_style === 'filled' ? '#ffffff' : template.accent_color,
                borderWidth: template.button_style === 'outline' ? '2px' : '0',
                borderColor: template.accent_color,
                borderStyle: 'solid'
              }}
            >
              <Heart className="w-4 h-4 inline mr-1" />
              Send Interest
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminProfileCardDesigner = () => {
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState('classic');
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [templatesRes, activeRes, previewRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/profile-card-design/templates`, { headers }),
        fetch(`${API_URL}/api/admin/profile-card-design/active`, { headers }),
        fetch(`${API_URL}/api/admin/profile-card-design/preview-data`, { headers })
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }

      if (activeRes.ok) {
        const data = await activeRes.json();
        setActiveTemplateId(data.active_template_id);
        setCurrentTemplate(data.template);
      }

      if (previewRes.ok) {
        const data = await previewRes.json();
        setPreviewUsers(data.preview_users || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({ title: "Error", description: "Failed to load templates", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setCurrentTemplate({ ...template });
      setHasChanges(templateId !== activeTemplateId);
    }
  };

  const updateTemplate = (key, value) => {
    setCurrentTemplate(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const updateField = (fieldId, key, value) => {
    setCurrentTemplate(prev => {
      const fields = [...(prev.fields || [])];
      const fieldIndex = fields.findIndex(f => f.field_id === fieldId);
      if (fieldIndex >= 0) {
        fields[fieldIndex] = { ...fields[fieldIndex], [key]: value };
      } else {
        fields.push({ field_id: fieldId, enabled: true, order: fields.length, [key]: value });
      }
      return { ...prev, fields };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!currentTemplate) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Save template changes
      await fetch(`${API_URL}/api/admin/profile-card-design/templates/${currentTemplate.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(currentTemplate)
      });

      // Set as active
      await fetch(`${API_URL}/api/admin/profile-card-design/active/${currentTemplate.id}`, {
        method: 'PUT',
        headers
      });

      setActiveTemplateId(currentTemplate.id);
      setHasChanges(false);
      toast({ title: "Saved!", description: "Card design applied to your site" });
      loadData();
    } catch (error) {
      console.error('Save failed:', error);
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const template = templates.find(t => t.id === activeTemplateId);
    if (template) {
      setCurrentTemplate({ ...template });
      setHasChanges(false);
    }
  };

  const createNewTemplate = async () => {
    const newTemplate = {
      ...currentTemplate,
      name: `Custom Template ${templates.filter(t => !t.is_system).length + 1}`,
      description: 'My custom card design'
    };

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/profile-card-design/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTemplate)
      });

      if (response.ok) {
        const data = await response.json();
        toast({ title: "Created!", description: "New template created" });
        loadData();
        setCurrentTemplate(data.template);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create template", variant: "destructive" });
    }
  };

  const fieldOptions = [
    { id: 'age', label: 'Age', icon: User },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'profession', label: 'Profession', icon: Briefcase },
    { id: 'education', label: 'Education', icon: GraduationCap },
    { id: 'religion', label: 'Religion', icon: Heart },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-profile-card-designer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile Card Designer</h1>
          <p className="text-gray-600 mt-1">Customize how user profiles appear on your site</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
          )}
          <Button onClick={createNewTemplate} variant="outline">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            className="bg-mint-500 hover:bg-mint-600"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save & Apply
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Template Selector */}
        <div className="xl:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Layout className="w-4 h-4" /> Templates
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-2">
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => selectTemplate(template.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                      currentTemplate?.id === template.id
                        ? 'bg-mint-100 border-2 border-mint-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg"
                      style={{ backgroundColor: template.accent_color }}
                    >
                      {template.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{template.name}</p>
                        {template.id === activeTemplateId && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Active</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{template.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customization Panel */}
        <div className="xl:col-span-2">
          {currentTemplate && (
            <div className="space-y-4">
              {/* Template Name */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Template Name</Label>
                      <Input
                        value={currentTemplate.name}
                        onChange={(e) => updateTemplate('name', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={currentTemplate.description || ''}
                        onChange={(e) => updateTemplate('description', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Layout Settings */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Layout className="w-4 h-4" /> Layout
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Card Layout</Label>
                      <select
                        value={currentTemplate.layout}
                        onChange={(e) => updateTemplate('layout', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="vertical">Vertical</option>
                        <option value="horizontal">Horizontal</option>
                        <option value="photo_overlay">Photo Overlay</option>
                      </select>
                    </div>
                    <div>
                      <Label>Photo Shape</Label>
                      <select
                        value={currentTemplate.photo_shape}
                        onChange={(e) => updateTemplate('photo_shape', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="circle">Circle</option>
                        <option value="rounded">Rounded</option>
                        <option value="square">Square</option>
                      </select>
                    </div>
                    <div>
                      <Label>Photo Size</Label>
                      <select
                        value={currentTemplate.photo_size}
                        onChange={(e) => updateTemplate('photo_size', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                    <div>
                      <Label>Corner Radius</Label>
                      <select
                        value={currentTemplate.border_radius}
                        onChange={(e) => updateTemplate('border_radius', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="none">None</option>
                        <option value="sm">Small</option>
                        <option value="md">Medium</option>
                        <option value="lg">Large</option>
                        <option value="xl">Extra Large</option>
                      </select>
                    </div>
                    <div>
                      <Label>Shadow</Label>
                      <select
                        value={currentTemplate.shadow}
                        onChange={(e) => updateTemplate('shadow', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="none">None</option>
                        <option value="sm">Small</option>
                        <option value="md">Medium</option>
                        <option value="lg">Large</option>
                        <option value="xl">Extra Large</option>
                      </select>
                    </div>
                    <div>
                      <Label>Padding</Label>
                      <select
                        value={currentTemplate.padding}
                        onChange={(e) => updateTemplate('padding', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="2">Compact</option>
                        <option value="3">Small</option>
                        <option value="4">Normal</option>
                        <option value="5">Large</option>
                        <option value="6">Extra Large</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Colors */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Palette className="w-4 h-4" /> Colors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs">Background</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={currentTemplate.background_color}
                          onChange={(e) => updateTemplate('background_color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={currentTemplate.background_color}
                          onChange={(e) => updateTemplate('background_color', e.target.value)}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Text</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={currentTemplate.text_color}
                          onChange={(e) => updateTemplate('text_color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={currentTemplate.text_color}
                          onChange={(e) => updateTemplate('text_color', e.target.value)}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Name</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={currentTemplate.name_color || currentTemplate.text_color || '#000000'}
                          onChange={(e) => updateTemplate('name_color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={currentTemplate.name_color || currentTemplate.text_color || '#000000'}
                          onChange={(e) => updateTemplate('name_color', e.target.value)}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Accent</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={currentTemplate.accent_color}
                          onChange={(e) => updateTemplate('accent_color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={currentTemplate.accent_color}
                          onChange={(e) => updateTemplate('accent_color', e.target.value)}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Border</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={currentTemplate.border_color}
                          onChange={(e) => updateTemplate('border_color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={currentTemplate.border_color}
                          onChange={(e) => updateTemplate('border_color', e.target.value)}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Typography */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Type className="w-4 h-4" /> Typography
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Name Size</Label>
                      <select
                        value={currentTemplate.name_font_size}
                        onChange={(e) => updateTemplate('name_font_size', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="sm">Small</option>
                        <option value="md">Medium</option>
                        <option value="lg">Large</option>
                        <option value="xl">Extra Large</option>
                      </select>
                    </div>
                    <div>
                      <Label>Name Weight</Label>
                      <select
                        value={currentTemplate.name_font_weight}
                        onChange={(e) => updateTemplate('name_font_weight', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="normal">Normal</option>
                        <option value="medium">Medium</option>
                        <option value="semibold">Semibold</option>
                        <option value="bold">Bold</option>
                      </select>
                    </div>
                    <div>
                      <Label>Detail Size</Label>
                      <select
                        value={currentTemplate.detail_font_size}
                        onChange={(e) => updateTemplate('detail_font_size', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="xs">Extra Small</option>
                        <option value="sm">Small</option>
                        <option value="md">Medium</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Fields */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4" /> Display Fields
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {fieldOptions.map((field, index) => {
                      const fieldConfig = (currentTemplate.fields || []).find(f => f.field_id === field.id) || { enabled: index < 3, order: index };
                      return (
                        <div key={field.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <field.icon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">{field.label}</span>
                          </div>
                          <Switch
                            checked={fieldConfig.enabled}
                            onCheckedChange={(checked) => updateField(field.id, 'enabled', checked)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Badges & Features */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Badges & Features
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-500" />
                        <span className="text-sm">Premium Badge</span>
                      </div>
                      <Switch
                        checked={currentTemplate.show_premium_badge}
                        onCheckedChange={(c) => updateTemplate('show_premium_badge', c)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">Verified Badge</span>
                      </div>
                      <Switch
                        checked={currentTemplate.show_verified_badge}
                        onCheckedChange={(c) => updateTemplate('show_verified_badge', c)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Wifi className="w-4 h-4 text-green-500" />
                        <span className="text-sm">Online Status</span>
                      </div>
                      <Switch
                        checked={currentTemplate.show_online_status}
                        onCheckedChange={(c) => updateTemplate('show_online_status', c)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">Show Bio</span>
                      </div>
                      <Switch
                        checked={currentTemplate.show_bio}
                        onCheckedChange={(c) => updateTemplate('show_bio', c)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-red-500" />
                        <span className="text-sm">Interest Button</span>
                      </div>
                      <Switch
                        checked={currentTemplate.show_interest_button}
                        onCheckedChange={(c) => updateTemplate('show_interest_button', c)}
                      />
                    </div>
                    {currentTemplate.show_interest_button && (
                      <div>
                        <Label className="text-xs">Button Style</Label>
                        <select
                          value={currentTemplate.button_style}
                          onChange={(e) => updateTemplate('button_style', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="filled">Filled</option>
                          <option value="outline">Outline</option>
                          <option value="text">Text Only</option>
                        </select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Live Preview */}
        <div className="xl:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="w-4 h-4" /> Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentTemplate && previewUsers.length > 0 && (
                <div className="space-y-4">
                  {previewUsers.slice(0, 2).map(user => (
                    <ProfileCardPreview 
                      key={user.id} 
                      template={currentTemplate} 
                      user={user} 
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminProfileCardDesigner;
